// src/pages/supplier/SmartPricingPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supplierPricingApi } from '../../api/services'
import {
  Card, CardHeader, CardBody, Badge, PageSpinner, Button,
} from '../../components/common/ui'
import {
  TrendingUp, TrendingDown, Minus, Check, X, RefreshCw,
  Zap, AlertCircle, ChevronDown, ChevronUp, Info,
} from 'lucide-react'

const fmt = v => `₹${Number(v || 0).toLocaleString('en-IN')}`

const POSITION_STYLES = {
  budget:      { bg: 'bg-green-50',  text: 'text-green-700',  label: 'Budget' },
  competitive: { bg: 'bg-blue-50',   text: 'text-blue-700',   label: 'Competitive' },
  premium:     { bg: 'bg-amber-50',  text: 'text-amber-700',  label: 'Premium' },
  luxury:      { bg: 'bg-purple-50', text: 'text-purple-700', label: 'Luxury' },
}

function ChangeIndicator({ pct }) {
  if (pct === null || pct === undefined) return null
  const abs = Math.abs(pct)
  if (abs <= 2) return <span className="flex items-center gap-0.5 text-xs text-gray-400 font-bold"><Minus size={11} /> ~0%</span>
  if (pct > 0) return <span className="flex items-center gap-0.5 text-xs text-green-600 font-bold"><TrendingUp size={11} /> +{pct}%</span>
  return <span className="flex items-center gap-0.5 text-xs text-red-500 font-bold"><TrendingDown size={11} /> {pct}%</span>
}

function CommodityRates({ commodity }) {
  if (!commodity) return null
  const rates = [
    ['Cotton', commodity.cotton],
    ['Silk',   commodity.silk],
    ['Wool',   commodity.wool],
    ['Linen',  commodity.linen],
    ['Polyester', commodity.polyester],
  ].filter(([, v]) => v)

  return (
    <Card>
      <CardHeader><span className="font-bold text-sm">Commodity Rates (per kg)</span></CardHeader>
      <CardBody className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {rates.map(([name, rate]) => (
          <div key={name} className="text-center">
            <p className="text-xs text-muted uppercase font-semibold">{name}</p>
            <p className="text-sm font-bold">{fmt(rate)}</p>
          </div>
        ))}
      </CardBody>
    </Card>
  )
}

function PriceBar({ current, suggested, median, p25, p75 }) {
  if (!median) return null
  const all = [current, suggested, median, p25, p75].filter(Boolean)
  const min = Math.min(...all) * 0.85
  const max = Math.max(...all) * 1.15
  const range = max - min || 1
  const pos = v => `${((v - min) / range) * 100}%`

  return (
    <div className="relative h-6 mt-2 mb-1">
      {/* Market range bar */}
      {p25 && p75 && (
        <div className="absolute top-2 h-2 bg-gray-100 rounded-full" style={{ left: pos(p25), width: `${((p75 - p25) / range) * 100}%` }}>
          <div className="absolute inset-0 bg-blue-100 rounded-full" />
        </div>
      )}
      {/* Median marker */}
      {median && (
        <div className="absolute top-0 w-0.5 h-6 bg-blue-400" style={{ left: pos(median) }}>
          <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-[9px] text-blue-500 font-bold whitespace-nowrap">MKT</span>
        </div>
      )}
      {/* Current price */}
      <div className="absolute top-1 w-3 h-3 rounded-full bg-gray-400 border-2 border-white shadow-sm" style={{ left: pos(current), transform: 'translateX(-50%)' }} />
      {/* Suggested price */}
      {suggested && (
        <div className="absolute top-1 w-3 h-3 rounded-full bg-pink border-2 border-white shadow-sm" style={{ left: pos(suggested), transform: 'translateX(-50%)' }} />
      )}
    </div>
  )
}

function ProductRow({ item, onAccept, onDismiss, acceptLoading, dismissLoading }) {
  const [expanded, setExpanded] = useState(false)
  const s = item.suggestion
  const posStyle = s ? POSITION_STYLES[s.position] || POSITION_STYLES.competitive : null

  return (
    <div className="border-b border-border last:border-b-0">
      <div
        className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-surface/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Product info */}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate">{item.name}</p>
          <p className="text-[10px] text-muted">{item.sku} &middot; {item.category} &middot; {item.fabric_type} &middot; {item.gsm}GSM</p>
        </div>

        {/* Current price */}
        <div className="text-right w-24 flex-shrink-0">
          <p className="text-xs text-muted">Current</p>
          <p className="font-bold text-sm">{fmt(item.current_retail)}</p>
        </div>

        {/* Suggested price */}
        <div className="text-right w-24 flex-shrink-0">
          <p className="text-xs text-muted">Suggested</p>
          {s ? (
            <p className="font-bold text-sm text-pink">{fmt(s.suggested_retail)}</p>
          ) : (
            <p className="text-xs text-muted">--</p>
          )}
        </div>

        {/* Change */}
        <div className="w-16 flex-shrink-0 text-right">
          {s && <ChangeIndicator pct={s.change_pct} />}
        </div>

        {/* Position badge */}
        <div className="w-24 flex-shrink-0 text-center">
          {posStyle && (
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${posStyle.bg} ${posStyle.text}`}>
              {posStyle.label}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 w-24 flex-shrink-0 justify-end">
          {s && (
            <>
              <button
                onClick={e => { e.stopPropagation(); onAccept(s.id) }}
                disabled={acceptLoading}
                className="w-7 h-7 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 flex items-center justify-center transition-colors disabled:opacity-50"
                title="Accept suggestion"
              >
                <Check size={14} />
              </button>
              <button
                onClick={e => { e.stopPropagation(); onDismiss(s.id) }}
                disabled={dismissLoading}
                className="w-7 h-7 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center transition-colors disabled:opacity-50"
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </>
          )}
          {expanded ? <ChevronUp size={14} className="text-muted" /> : <ChevronDown size={14} className="text-muted" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && s && (
        <div className="px-5 pb-4 space-y-3">
          {/* Price position bar */}
          <div className="bg-surface rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold text-muted">Price Position</span>
              <div className="flex gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" /> Current</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-pink" /> Suggested</span>
                <span className="flex items-center gap-1"><span className="w-1 h-3 bg-blue-400" /> Market median</span>
              </div>
            </div>
            <PriceBar
              current={item.current_retail}
              suggested={s.suggested_retail}
              median={s.market_median}
              p25={s.signals?.market_p25}
              p75={s.signals?.market_p75}
            />
            <div className="flex justify-between text-[10px] text-muted mt-1">
              <span>P25: {fmt(s.signals?.market_p25)}</span>
              <span>Median: {fmt(s.market_median)}</span>
              <span>P75: {fmt(s.signals?.market_p75)}</span>
            </div>
          </div>

          {/* Rationale */}
          <div className="flex items-start gap-2 text-xs bg-blue-50 p-3 rounded-xl">
            <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-blue-800">{s.rationale}</p>
          </div>

          {/* Signal details */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-surface rounded-lg p-3">
              <p className="text-[10px] text-muted uppercase">Cost Floor</p>
              <p className="text-sm font-bold">{fmt(s.signals?.cost_floor)}/m</p>
            </div>
            <div className="bg-surface rounded-lg p-3">
              <p className="text-[10px] text-muted uppercase">Demand Score</p>
              <p className="text-sm font-bold">{s.signals?.demand_score ?? '--'}/100</p>
            </div>
            <div className="bg-surface rounded-lg p-3">
              <p className="text-[10px] text-muted uppercase">Wholesale Suggested</p>
              <p className="text-sm font-bold">{fmt(s.suggested_wholesale)}</p>
            </div>
            <div className="bg-surface rounded-lg p-3">
              <p className="text-[10px] text-muted uppercase">Last Computed</p>
              <p className="text-sm font-bold">{s.computed_at ? new Date(s.computed_at).toLocaleDateString('en-IN') : '--'}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4 text-xs text-muted">
            <span>{item.views} views</span>
            <span>{item.orders} orders</span>
            <span>Conversion: {item.views > 0 ? ((item.orders / item.views) * 100).toFixed(1) : 0}%</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default function SmartPricingPage() {
  const [filter, setFilter] = useState('all') // all, underpriced, overpriced, well_priced
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-pricing'],
    queryFn: () => supplierPricingApi.index(),
  })

  const computeMut = useMutation({
    mutationFn: () => supplierPricingApi.compute(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['supplier-pricing'] }),
  })

  const acceptMut = useMutation({
    mutationFn: (id) => supplierPricingApi.accept(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['supplier-pricing'] }),
  })

  const dismissMut = useMutation({
    mutationFn: (id) => supplierPricingApi.dismiss(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['supplier-pricing'] }),
  })

  if (isLoading) return <PageSpinner />

  const payload = data?.data?.data || {}
  const items = payload.items || []
  const summary = payload.summary || {}
  const commodity = payload.commodity || {}

  // Filter items
  const filtered = items.filter(item => {
    if (filter === 'all') return true
    if (!item.suggestion) return false
    const pct = item.suggestion.change_pct
    if (filter === 'underpriced') return pct > 5
    if (filter === 'overpriced') return pct < -5
    if (filter === 'well_priced') return Math.abs(pct) <= 5
    return true
  })

  return (
    <div className="space-y-6 max-w-[1200px]">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-display font-bold">Smart Pricing</h1>
          <p className="text-xs text-muted mt-0.5">AI-powered pricing suggestions based on market data, costs, and demand</p>
        </div>
        <Button
          variant="primary"
          size="sm"
          loading={computeMut.isPending}
          onClick={() => computeMut.mutate()}
        >
          <Zap size={14} /> Recompute Prices
        </Button>
      </div>

      {computeMut.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-800">
          {computeMut.data?.data?.data?.message || 'Prices recomputed successfully.'}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4 cursor-pointer hover:border-pink transition-colors" onClick={() => setFilter('all')}>
          <p className="text-xs text-muted font-semibold uppercase">Total Products</p>
          <p className="text-2xl font-extrabold font-display">{summary.total_products || 0}</p>
        </Card>
        <Card className={`p-4 cursor-pointer transition-colors ${filter === 'underpriced' ? 'border-green-400' : 'hover:border-green-400'}`} onClick={() => setFilter('underpriced')}>
          <p className="text-xs text-green-600 font-semibold uppercase flex items-center gap-1"><TrendingUp size={11} /> Underpriced</p>
          <p className="text-2xl font-extrabold font-display text-green-600">{summary.underpriced || 0}</p>
          <p className="text-[10px] text-muted">Can increase price</p>
        </Card>
        <Card className={`p-4 cursor-pointer transition-colors ${filter === 'overpriced' ? 'border-red-400' : 'hover:border-red-400'}`} onClick={() => setFilter('overpriced')}>
          <p className="text-xs text-red-500 font-semibold uppercase flex items-center gap-1"><TrendingDown size={11} /> Overpriced</p>
          <p className="text-2xl font-extrabold font-display text-red-500">{summary.overpriced || 0}</p>
          <p className="text-[10px] text-muted">Consider reducing</p>
        </Card>
        <Card className={`p-4 cursor-pointer transition-colors ${filter === 'well_priced' ? 'border-blue-400' : 'hover:border-blue-400'}`} onClick={() => setFilter('well_priced')}>
          <p className="text-xs text-blue-600 font-semibold uppercase flex items-center gap-1"><Minus size={11} /> Well Priced</p>
          <p className="text-2xl font-extrabold font-display text-blue-600">{summary.well_priced || 0}</p>
          <p className="text-[10px] text-muted">Within 5% of optimal</p>
        </Card>
      </div>

      {/* Commodity rates */}
      <CommodityRates commodity={commodity} />

      {/* Products list */}
      <Card>
        <CardHeader>
          <span className="font-bold text-sm">
            {filter === 'all' ? 'All Products' : filter === 'underpriced' ? 'Underpriced Products' : filter === 'overpriced' ? 'Overpriced Products' : 'Well-Priced Products'}
            <span className="text-muted font-normal ml-2">({filtered.length})</span>
          </span>
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')} className="text-xs text-pink font-bold hover:underline">Show all</button>
          )}
        </CardHeader>
        <div>
          {/* Column headers */}
          <div className="px-5 py-2 flex items-center gap-4 text-[10px] text-muted uppercase font-semibold tracking-wide border-b border-border bg-surface/50">
            <div className="flex-1">Product</div>
            <div className="w-24 text-right">Current</div>
            <div className="w-24 text-right">Suggested</div>
            <div className="w-16 text-right">Change</div>
            <div className="w-24 text-center">Position</div>
            <div className="w-24 text-right">Actions</div>
          </div>

          {filtered.length === 0 ? (
            <div className="py-12 text-center">
              <AlertCircle size={24} className="text-muted mx-auto mb-2" />
              <p className="text-sm text-muted">
                {items.length === 0
                  ? 'No active products. Add products to get pricing suggestions.'
                  : 'No products match this filter.'}
              </p>
              {items.length > 0 && summary.with_suggestions === 0 && (
                <Button variant="primary" size="sm" className="mt-4" onClick={() => computeMut.mutate()} loading={computeMut.isPending}>
                  <Zap size={14} /> Generate Suggestions
                </Button>
              )}
            </div>
          ) : (
            filtered.map(item => (
              <ProductRow
                key={item.product_id}
                item={item}
                onAccept={(id) => acceptMut.mutate(id)}
                onDismiss={(id) => dismissMut.mutate(id)}
                acceptLoading={acceptMut.isPending}
                dismissLoading={dismissMut.isPending}
              />
            ))
          )}
        </div>
      </Card>

      {/* Guide */}
      <Card>
        <CardHeader><span className="font-bold text-sm">How Smart Pricing Works</span></CardHeader>
        <CardBody className="text-xs text-muted space-y-2">
          <p><strong className="text-gray-700">Cost Floor:</strong> Calculated from raw material commodity rates (cotton, silk, etc.), GSM, fabric width, labour costs, platform commission, and minimum gross margin.</p>
          <p><strong className="text-gray-700">Market Position:</strong> Your price is compared against the category median, P25, and P75 from daily market snapshots across the platform.</p>
          <p><strong className="text-gray-700">Demand Adjustment:</strong> Prices are adjusted up/down based on real-time demand signals — search volume, view velocity, and order conversion rates.</p>
          <p><strong className="text-gray-700">Accepting a Suggestion:</strong> Updates your retail and wholesale price immediately. You can always change prices manually from the product edit page.</p>
        </CardBody>
      </Card>
    </div>
  )
}
